import {
  AfterViewInit, Component, ElementRef, EventEmitter, Input, OnChanges, OnDestroy, Output, ViewChild,
} from '@angular/core';
import * as echarts from 'echarts/core';
import { BarChart, LineChart, PieChart } from 'echarts/charts';
import {
  DatasetComponent, GridComponent, LegendComponent, TitleComponent, TooltipComponent,
} from 'echarts/components';
import { CanvasRenderer } from 'echarts/renderers';
import type { EChartsCoreOption } from 'echarts/core';

echarts.use([
  BarChart, LineChart, PieChart,
  GridComponent, TooltipComponent, LegendComponent, TitleComponent, DatasetComponent,
  CanvasRenderer,
]);

/**
 * O que se clicou num gráfico. `payload` é o que a página pendurou no ponto ao construir a
 * série — normalmente o filtro que este pedaço representa.
 */
export interface EChartPoint {
  readonly seriesName?: string;
  readonly name: string;
  readonly value: unknown;
  readonly payload?: Record<string, unknown>;
}

/**
 * Wrapper fino do Apache ECharts (Etapa 7): a página constrói as options (já
 * com as cores do tema) e este componente só renderiza, re-aplica quando as
 * options mudam e faz resize com o contentor.
 *
 * <p>Emite também o clique num ponto. O gráfico responde a "quantos" — a pergunta a seguir é
 * sempre "quais", e é a página que sabe responder-lhe.
 */
@Component({
  selector: 'app-echart',
  template: '<div #host [style.height.px]="height" style="width: 100%;"></div>',
})
export class EChart implements AfterViewInit, OnChanges, OnDestroy {
  @Input({ required: true }) options!: EChartsCoreOption;
  @Input() height = 300;

  /** Ponto clicado, com o que a página lhe pendurou. */
  @Output() pointClick = new EventEmitter<EChartPoint>();

  @ViewChild('host', { static: true }) host!: ElementRef<HTMLDivElement>;

  private chart?: echarts.ECharts;
  private resizeObserver?: ResizeObserver;

  ngAfterViewInit(): void {
    this.chart = echarts.init(this.host.nativeElement);
    this.chart.on('click', (params) => {
      const data = params.data as { drill?: Record<string, unknown> } | undefined;
      this.pointClick.emit({
        seriesName: params.seriesName,
        name: String(params.name ?? ''),
        value: params.value,
        // `drill` é a convenção: a página põe-no no ponto, e volta cá intacto.
        payload: data && typeof data === 'object' ? data.drill : undefined,
      });
    });
    this.apply();
    this.resizeObserver = new ResizeObserver(() => this.chart?.resize());
    this.resizeObserver.observe(this.host.nativeElement);
  }

  ngOnChanges(): void {
    this.apply();
  }

  ngOnDestroy(): void {
    this.resizeObserver?.disconnect();
    this.chart?.dispose();
  }

  /**
   * Aplica as options novas transitando a partir das antigas.
   *
   * <p>Com `notMerge` o ECharts deitava o gráfico fora e desenhava outro: mudar um filtro fazia
   * as barras desaparecer e reaparecer já no tamanho novo. Substituindo só as partes que mudam
   * de forma, ele reconhece os pontos e anima-os — as barras crescem e encolhem.
   *
   * <p>`replaceMerge` e não uma fusão simples: quando o número de séries ou de grelhas diminui,
   * uma fusão deixava as antigas lá, sobrepostas às novas.
   */
  private apply(): void {
    if (this.chart && this.options) {
      this.chart.setOption(this.options, {
        replaceMerge: ['series', 'xAxis', 'yAxis', 'grid', 'title', 'legend'],
      });
    }
  }
}
